/**
 * tools-login — Redirect von DAM/Transfer auf den zentralen Login (P1c PR-C,
 * Must 10, D-P1c-3).
 *
 * Ab dem Cutover haben DAM und Transfer keine Credential-Routen mehr
 * (`authIdpRoutes` liegt nur noch auf tools). Ein unauthentifizierter Client
 * rendert deshalb keine LoginPage mehr, sondern verlässt die App Richtung
 * `tools.greenforce.com/login?app=<id>&next=<pfad>`. Der tools-Login mintet nach
 * erfolgreicher Anmeldung automatisch den SSO-Handoff zurück auf den Deep-Link
 * (PR-A, Must 2).
 *
 * Warum die Ziel-Origin hier hart steht und NICHT aus `sso-targets.ts` kommt:
 * das Modul ist server-only (zieht `lib/env.js` → `process.exit` beim Parsen)
 * und darf nicht in ein Client-Bundle. Die Werte sind bewusst dieselben
 * Canonical-Origins wie dort — in Prod so stabil wie die `/s`/`/t`-URLs, kein
 * Env-Knob = kein Misconfig-Vektor.
 */
import { sanitizeNextPath } from '../../shared/sanitize-next-path';

/** Die beiden Consumer-Apps, die auf tools zurückfallen. tools selbst nie. */
export type ToolsLoginApp = 'dam' | 'transfer';

const PROD_TOOLS_ORIGIN = 'https://tools.greenforce.com';
/** Dev: Vite-Client-Port des tools-Builds (package.json `dev:client:tools`). */
const DEV_TOOLS_ORIGIN = 'http://127.0.0.1:5175';

/**
 * Prod-Erkennung an der Origin des LAUFENDEN Clients, nicht am Build:
 * ein Artefakt für alle Umgebungen (gleiche Regel wie `appLinks.ts`).
 * `assets.greenforce.com` / `transfer.greenforce.com` → Prod-tools;
 * alles andere (localhost, 127.0.0.1, Preview) → Dev-tools.
 *
 * Exakter Suffix-Match auf `.greenforce.com` — ein blosses `includes()` würde
 * bei `evil-greenforce.com.attacker.tld` mitziehen.
 */
export function toolsOrigin(hostname: string): string {
  return hostname === 'greenforce.com' || hostname.endsWith('.greenforce.com')
    ? PROD_TOOLS_ORIGIN
    : DEV_TOOLS_ORIGIN;
}

/**
 * Baut den `next`-Pfad für den Rücksprung: aktueller Pfad + Query.
 *
 * Zwei Schutzschichten:
 *  - `sanitizeNextPath` (shared mit Mint + Consumer) killt protocol-relative
 *    und schema-artige Werte. Hier kommt der Wert zwar aus `location`, ist also
 *    per Konstruktion app-relativ — die Sanitisierung bleibt trotzdem, damit
 *    der Helper an JEDER Callsite sicher ist und nicht nur an den heutigen.
 *  - `/login` → `/`: der Pfad existiert nach dem Cutover nicht mehr; ohne das
 *    Mapping schickte der Handoff den User auf eine tote Route zurück.
 *
 * Der Hash (`#…`) fliegt bewusst raus: er wird ohnehin nie an den Server
 * geschickt und überlebt die Handoff-Kette (POST → 302) nicht.
 */
export function buildNextPath(pathname: string, search: string): string {
  const raw = sanitizeNextPath(`${pathname}${search}`);
  if (raw === '/login' || raw.startsWith('/login?')) return '/';
  return raw;
}

/** Vollständige tools-Login-URL für eine App + einen Rücksprung-Pfad. */
export function toolsLoginUrl(app: ToolsLoginApp, hostname: string, next: string): string {
  return `${toolsOrigin(hostname)}/login?app=${app}&next=${encodeURIComponent(next)}`;
}

/**
 * Verlässt die App Richtung tools-Login. `replace()` statt `assign()`, damit der
 * Zurück-Button nicht in die (nicht mehr existierende) Login-Route der
 * Consumer-App zurückfällt.
 *
 * `app` wird an der Callsite als Literal übergeben (`'dam'` in `App.tsx`,
 * `'transfer'` in `TransferApp.tsx`) — nicht aus der Umgebung abgeleitet, damit
 * jede App unmissverständlich ihre eigene Identität schickt.
 */
export function redirectToToolsLogin(app: ToolsLoginApp): void {
  const { pathname, search, hostname } = window.location;
  window.location.replace(toolsLoginUrl(app, hostname, buildNextPath(pathname, search)));
}

/**
 * Globaler Logout (Codex-PR-C-Finding): DAM/Transfer schicken den User nach dem
 * lokalen Cookie-Clear auf `tools/logout`, wo die IdP-Session selbst beendet
 * wird.
 *
 * Ohne diesen Schritt wäre „Abmelden" nach dem Cutover wirkungslos: der lokale
 * Logout löscht nur das App-Cookie, der unauthed-Branch redirectet nach
 * `tools/login?app=…`, tools ist dort noch angemeldet und mintet laut PR-A
 * SOFORT einen Auto-Handoff zurück — der User wäre nach einem sichtbaren
 * Rundflug wieder eingeloggt. Auf einem geteilten Rechner ist das kein
 * UX-Schönheitsfehler, sondern eine offene Session.
 *
 * Bewusst OHNE `app`/`next`-Parameter: die tragen genau die Auto-Handoff-Semantik,
 * die hier nicht gewollt ist.
 */
export function redirectToToolsLogout(): void {
  window.location.replace(`${toolsOrigin(window.location.hostname)}/logout`);
}

/**
 * Echte Browser-Navigation zu einer absoluten URL.
 *
 * Existiert als eigene Funktion, weil `window.location.assign` in jsdom
 * non-configurable ist und sich nicht spionieren lässt — ein Konsument könnte
 * seinen Cross-Origin-Pfad sonst gar nicht testen. Hier ist es eine
 * Modul-Grenze, die ein Test mocken kann.
 */
export function hardNavigate(url: string): void {
  window.location.assign(url);
}

/** Die tools-Settings-Tabs, die DAM ab dem Cutover abgibt (Must 12, D-P1c-9). */
export type ToolsSettingsTab = 'profile' | 'team';

/** Vollständige URL eines tools-Settings-Tabs. */
export function toolsSettingsUrl(tab: ToolsSettingsTab, hostname: string): string {
  return `${toolsOrigin(hostname)}/settings/${tab}`;
}

/**
 * Schickt den User in die tools-Settings (Profil bzw. Team). Cross-Origin →
 * bewusst `window.location.replace()` statt eines wouter-`<Redirect>`: wouter
 * kennt nur den App-eigenen History-Stack und würde die fremde Origin gar nicht
 * erreichen. `replace` statt `assign`, damit der Zurück-Button nicht in die
 * gerade verlassene DAM-Settings-Route zurückspringt.
 */
export function redirectToToolsSettings(tab: ToolsSettingsTab): void {
  window.location.replace(toolsSettingsUrl(tab, window.location.hostname));
}
